/**
 * 저장 확인 기록 durable spool (TASK-2026-004 CR-01 A+, ADR-002).
 *
 * ## 계층
 *
 * 1. **hot buffer**: 계측 로그의 동기 sink를 받는 메모리 버퍼(`createConfirmationBuffer`).
 * 2. **runtime ledger**: 이 탭이 받았지만 아직 spool에 넣지 못한 수를 localStorage에 동기로 적는다.
 *    pump가 돌기 전에 페이지가 닫혀도 다음 runtime이 그 수를 손실로 **셀 수 있다**.
 * 3. **session spool**: 세션의 미전송 기록·누적 손실·상태의 원본. 탭·페이지가 함께 쓴다.
 *
 * 규칙(상한·손실 누적·상태 전이·정산)은 이 파일의 순수 계획 함수에 있다. backend는 한
 * transaction 안에서 현재 모습을 읽어 계획을 받고 그대로 적용하기만 한다. 그래서 IndexedDB
 * adapter와 검사용 메모리 backend가 같은 규칙을 따른다.
 *
 * 확인 기록 저장은 계측 로그 저장 hook을 거치지 않는다. spool이 쓰는 것은 backend와 ledger
 * 키뿐이므로 확인 기록이 확인 기록을 다시 만들지 않는다.
 */

import { createConfirmationBuffer, validateConfirmation, type SaveConfirmation } from './confirmation';

export const SPOOL_META_SCHEMA = 'ccatlas.confirmation-spool-meta/1';
export const RUNTIME_LEDGER_SCHEMA = 'ccatlas.confirmation-runtime/1';
/** 세션당 spool 상한 (ADR-002 §3). */
export const SPOOL_CAPACITY = 2_000;
/** 이보다 오래 갱신되지 않은 runtime ledger는 닫힌 탭으로 보고 정산한다 (ADR-002 §5). */
export const RUNTIME_STALE_MS = 15_000;
/** hot buffer 상한. spool 쓰기가 멈춘 동안만 찬다. */
export const HOT_CAPACITY = 500;

export const SPOOL_STATES = ['complete', 'loss', 'unverified'] as const;
export type SpoolState = (typeof SPOOL_STATES)[number];

export const UNVERIFIED_REASONS = [
  'storage_unavailable',
  'write_failed',
  'read_failed',
  'readback_mismatch',
  'corrupt',
  'ledger_unavailable',
  'ledger_conflict',
] as const;
export type UnverifiedReason = (typeof UNVERIFIED_REASONS)[number];

export interface SpoolMeta {
  readonly schema: typeof SPOOL_META_SCHEMA;
  readonly session_ref: string;
  /** 다음에 붙일 기록 ID. 세션 안에서 단조 증가한다. */
  readonly next_id: number;
  /** spool에 남은 기록 수. */
  readonly count: number;
  /** 누적 손실 수. 줄지 않는다. */
  readonly dropped: number;
  readonly state: SpoolState;
  readonly reason: UnverifiedReason | null;
  /** 보내는 중인 batch의 기록 ID. 상한 때문에 지우지 않는다. */
  readonly pinned: readonly number[];
}

export interface SpooledRecord {
  readonly id: number;
  readonly record: SaveConfirmation;
}

/** backend가 transaction 안에서 읽은 모습. `meta`는 검증 전의 원래 값이다. */
export interface SpoolView {
  readonly meta: unknown;
  /** 이 세션의 기록 ID, 오름차순. */
  readonly ids: readonly number[];
}

export interface SpoolMutation {
  readonly meta: SpoolMeta;
  readonly put: readonly SpooledRecord[];
  readonly remove: readonly number[];
}

export class SpoolError extends Error {
  constructor(readonly reason: UnverifiedReason) {
    super(`confirmation spool: ${reason}`);
    this.name = 'SpoolError';
  }
}

/**
 * spool 저장소 계약. 한 호출이 한 transaction이다.
 *
 * - `transact`: 읽은 모습으로 `plan`을 부르고 결과를 원자적으로 적용한다. 적용이 끝나야
 *   resolve한다. 쓰기 실패·abort는 `SpoolError('write_failed')`로, `plan`이 던진 오류는
 *   아무것도 쓰지 않고 그대로 던진다.
 * - `list`: 이 세션의 meta와 가장 오래된 기록 `limit`건. 읽기 실패는 `SpoolError('read_failed')`.
 */
export interface SpoolBackend {
  transact(sessionRef: string, plan: (view: SpoolView) => SpoolMutation): Promise<SpoolMeta>;
  list(sessionRef: string, limit: number): Promise<{ readonly meta: unknown; readonly records: readonly unknown[] }>;
}

/** localStorage처럼 키를 열거할 수 있는 동기 저장소. runtime ledger가 쓴다. */
export interface LedgerStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): readonly string[];
}

export interface SpoolSnapshot {
  /** 서버 ACK를 기다리는 기록 수(spool + 아직 spool에 넣지 못한 hot 기록). */
  readonly pending: number;
  /** 누적 손실 수. */
  readonly dropped: number;
  readonly state: SpoolState;
  readonly reason: UnverifiedReason | null;
  /** `memory`면 spool을 쓸 수 없어 hot buffer를 원본으로 보낸다. */
  readonly mode: 'durable' | 'memory';
}

export interface ConfirmationSpool {
  readonly sessionRef: string;
  /** 계측 로그의 확인 기록 sink. 동기다. */
  sink(record: SaveConfirmation): void;
  /** spool을 열고 닫힌 runtime을 정산할 때까지 기다린다. */
  ready(): Promise<void>;
  /** hot buffer를 spool로 옮긴다. 진행 중인 옮기기가 있으면 끝까지 기다린다. */
  drain(): Promise<void>;
  /** 보낼 기록을 가장 오래된 것부터 `max`건. 지우지 않는다. */
  peek(max: number): Promise<readonly SpooledRecord[]>;
  /** 보내는 중인 batch의 기록 ID를 고정한다. 빈 목록이면 풀린다. */
  pin(ids: readonly number[]): Promise<void>;
  /** 서버 ACK를 받은 기록만 지우고 pin을 푼다. */
  commit(ids: readonly number[]): Promise<void>;
  snapshot(): SpoolSnapshot;
}

export interface SpoolOptions {
  readonly sessionRef: string;
  /** `undefined`면 저장소가 없다(`storage_unavailable`). */
  readonly backend: SpoolBackend | undefined;
  readonly ledger: LedgerStorage;
  readonly runtimeId: string;
  readonly now: () => number;
  readonly capacity?: number;
  readonly hotCapacity?: number;
  readonly staleMs?: number;
  /** pump를 예약한다. 기본은 다음 task. */
  readonly schedule?: (run: () => void) => void;
}

// ── 계획 함수 (순수) ─────────────────────────────────────────────────────

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

export function freshMeta(sessionRef: string): SpoolMeta {
  return {
    schema: SPOOL_META_SCHEMA,
    session_ref: sessionRef,
    next_id: 1,
    count: 0,
    dropped: 0,
    state: 'complete',
    reason: null,
    pinned: [],
  };
}

/** 저장된 meta를 계약대로 읽는다. 없으면 새 meta, 모양이 틀리면 `SpoolError('corrupt')`. */
export function readMeta(raw: unknown, sessionRef: string): SpoolMeta {
  if (raw === undefined || raw === null) {
    return freshMeta(sessionRef);
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) {
    throw new SpoolError('corrupt');
  }
  const value = raw as Record<string, unknown>;
  const state = value['state'];
  const reason = value['reason'];
  const pinned = value['pinned'];
  if (
    value['schema'] !== SPOOL_META_SCHEMA ||
    value['session_ref'] !== sessionRef ||
    !isCount(value['next_id']) ||
    (value['next_id'] as number) < 1 ||
    !isCount(value['count']) ||
    !isCount(value['dropped']) ||
    !SPOOL_STATES.includes(state as SpoolState) ||
    !(reason === null || UNVERIFIED_REASONS.includes(reason as UnverifiedReason)) ||
    (state === 'unverified') !== (reason !== null) ||
    (state === 'complete' && value['dropped'] !== 0) ||
    (state === 'loss' && value['dropped'] === 0) ||
    !Array.isArray(pinned) ||
    !pinned.every((id) => isCount(id) && id >= 1)
  ) {
    throw new SpoolError('corrupt');
  }
  return {
    schema: SPOOL_META_SCHEMA,
    session_ref: sessionRef,
    next_id: value['next_id'] as number,
    count: value['count'] as number,
    dropped: value['dropped'] as number,
    state: state as SpoolState,
    reason: reason as UnverifiedReason | null,
    pinned: pinned as number[],
  };
}

/** 손실·확인 불가를 반영한 상태. `unverified`는 되돌아가지 않는다. */
function settle(meta: SpoolMeta, dropped: number, unverified: UnverifiedReason | null): SpoolMeta {
  const reason = meta.reason ?? unverified;
  const state: SpoolState = reason !== null ? 'unverified' : dropped > 0 ? 'loss' : 'complete';
  return { ...meta, dropped, state, reason };
}

export function planAppend(
  view: SpoolView,
  sessionRef: string,
  records: readonly SaveConfirmation[],
  options: { readonly capacity: number; readonly extraDropped: number; readonly unverified: UnverifiedReason | null },
): SpoolMutation {
  const meta = readMeta(view.meta, sessionRef);
  const added: SpooledRecord[] = records.map((record, index) => ({ id: meta.next_id + index, record }));
  const all = [...view.ids, ...added.map((item) => item.id)];
  const pinned = new Set(meta.pinned);
  const overflow = Math.max(0, all.length - options.capacity);
  const evicted = new Set<number>();
  for (const id of all) {
    if (evicted.size >= overflow) {
      break;
    }
    if (!pinned.has(id)) {
      evicted.add(id);
    }
  }
  const existing = new Set(view.ids);
  const next = settle(
    { ...meta, next_id: meta.next_id + records.length, count: all.length - evicted.size },
    meta.dropped + options.extraDropped + evicted.size,
    options.unverified,
  );
  return {
    meta: next,
    put: added.filter((item) => !evicted.has(item.id)),
    remove: [...evicted].filter((id) => existing.has(id)),
  };
}

export function planRemove(view: SpoolView, sessionRef: string, ids: readonly number[]): SpoolMutation {
  const meta = readMeta(view.meta, sessionRef);
  const existing = new Set(view.ids);
  const remove = [...new Set(ids)].filter((id) => existing.has(id));
  return { meta: { ...meta, count: view.ids.length - remove.length, pinned: [] }, put: [], remove };
}

export function planPin(view: SpoolView, sessionRef: string, ids: readonly number[]): SpoolMutation {
  const meta = readMeta(view.meta, sessionRef);
  const existing = new Set(view.ids);
  return { meta: { ...meta, count: view.ids.length, pinned: ids.filter((id) => existing.has(id)) }, put: [], remove: [] };
}

export function planSettle(
  view: SpoolView,
  sessionRef: string,
  extraDropped: number,
  unverified: UnverifiedReason | null,
): SpoolMutation {
  const meta = readMeta(view.meta, sessionRef);
  return { meta: settle({ ...meta, count: view.ids.length }, meta.dropped + extraDropped, unverified), put: [], remove: [] };
}

// ── runtime ledger ───────────────────────────────────────────────────────

function ledgerPrefix(sessionRef: string): string {
  return `ccatlas.session.${sessionRef}.ccatlas.confirmation.runtime.`;
}

export function unverifiedFlagKey(sessionRef: string): string {
  return `ccatlas.session.${sessionRef}.ccatlas.confirmation.unverified`;
}

interface LedgerEntry {
  readonly unspooled: number;
  readonly hot_dropped: number;
  readonly heartbeat_at: number;
}

function parseLedger(raw: string | null): LedgerEntry | undefined {
  if (raw === null) {
    return undefined;
  }
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    if (
      value['schema'] === RUNTIME_LEDGER_SCHEMA &&
      isCount(value['unspooled']) &&
      isCount(value['hot_dropped']) &&
      isCount(value['heartbeat_at'])
    ) {
      return value as unknown as LedgerEntry;
    }
  } catch {
    // 아래로 떨어진다.
  }
  throw new SpoolError('corrupt');
}

// ── spool ────────────────────────────────────────────────────────────────

export function createConfirmationSpool(options: SpoolOptions): ConfirmationSpool {
  const { sessionRef, backend, ledger, runtimeId, now } = options;
  const capacity = options.capacity ?? SPOOL_CAPACITY;
  const staleMs = options.staleMs ?? RUNTIME_STALE_MS;
  const schedule = options.schedule ?? ((run: () => void) => void setTimeout(run, 0));
  const hot = createConfirmationBuffer(options.hotCapacity ?? HOT_CAPACITY);
  const ownLedgerKey = `${ledgerPrefix(sessionRef)}${runtimeId}`;

  let meta: SpoolMeta | undefined;
  let mode: 'durable' | 'memory' = backend === undefined ? 'memory' : 'durable';
  let unverified: UnverifiedReason | null = backend === undefined ? 'storage_unavailable' : null;
  /** hot buffer 손실 중 spool meta에 이미 더한 수. */
  let foldedHotDropped = 0;
  /** 정산된 다른 runtime의 손실 중 아직 spool에 더하지 못한 수(저장소를 못 쓸 때). */
  let unsettledDropped = 0;
  /** 이 runtime이 ledger에 미저장 수를 적어 두었는가. 사라지면 누군가 정산한 것이다. */
  let ledgerHeld = false;
  let pumpScheduled = false;
  let chain: Promise<void> = Promise.resolve();

  const hotIds = new WeakMap<SaveConfirmation, number>();
  let nextHotId = -1;

  function markUnverified(reason: UnverifiedReason): void {
    unverified ??= reason;
    try {
      ledger.setItem(unverifiedFlagKey(sessionRef), unverified);
    } catch {
      // 이미 확인 불가다. 이 사실을 더 적지 못해도 상태는 바뀌지 않는다.
    }
  }

  function hotDropped(): number {
    return hot.snapshot().dropped - foldedHotDropped;
  }

  function writeLedger(): void {
    const unspooled = hot.size();
    const dropped = hotDropped();
    try {
      if (unspooled === 0 && dropped === 0) {
        ledger.removeItem(ownLedgerKey);
        ledgerHeld = false;
        return;
      }
      ledger.setItem(
        ownLedgerKey,
        JSON.stringify({ schema: RUNTIME_LEDGER_SCHEMA, unspooled, hot_dropped: dropped, heartbeat_at: now() }),
      );
      ledgerHeld = true;
    } catch {
      markUnverified('ledger_unavailable');
    }
  }

  async function persistUnverified(): Promise<void> {
    if (backend === undefined || unverified === null) {
      return;
    }
    try {
      const reason = unverified;
      meta = await backend.transact(sessionRef, (view) => planSettle(view, sessionRef, 0, reason));
    } catch {
      // best-effort. localStorage 표식이 같은 사실을 들고 있다.
    }
  }

  const opened: Promise<void> = (async () => {
    try {
      const flag = ledger.getItem(unverifiedFlagKey(sessionRef));
      if (flag !== null && UNVERIFIED_REASONS.includes(flag as UnverifiedReason)) {
        unverified = flag as UnverifiedReason;
      }
    } catch {
      unverified ??= 'ledger_unavailable';
    }
    if (backend === undefined) {
      markUnverified('storage_unavailable');
      return;
    }

    // 닫힌 runtime의 미저장 수를 모은다.
    const stale: string[] = [];
    let lost = 0;
    try {
      for (const key of ledger.keys()) {
        if (!key.startsWith(ledgerPrefix(sessionRef)) || key === ownLedgerKey) {
          continue;
        }
        try {
          const entry = parseLedger(ledger.getItem(key));
          if (entry !== undefined && now() - entry.heartbeat_at > staleMs) {
            lost += entry.unspooled + entry.hot_dropped;
            stale.push(key);
          }
        } catch {
          markUnverified('corrupt');
        }
      }
    } catch {
      markUnverified('ledger_unavailable');
    }

    try {
      const current = await backend.list(sessionRef, 0);
      meta = readMeta(current.meta, sessionRef);
      if (meta.state === 'unverified' && meta.reason !== null) {
        unverified ??= meta.reason;
      }
    } catch (error) {
      markUnverified(error instanceof SpoolError ? error.reason : 'read_failed');
      mode = 'memory';
      unsettledDropped += lost;
      return;
    }

    if (lost > 0 || (unverified !== null && meta.state !== 'unverified')) {
      try {
        const reason = unverified;
        meta = await backend.transact(sessionRef, (view) => planSettle(view, sessionRef, lost, reason));
        for (const key of stale) {
          ledger.removeItem(key);
        }
      } catch (error) {
        markUnverified(error instanceof SpoolError ? error.reason : 'write_failed');
        unsettledDropped += lost;
      }
    }
  })();

  async function pump(): Promise<void> {
    await opened;
    if (mode === 'memory' || backend === undefined) {
      return;
    }
    const records = hot.peek(hot.size());
    const extraDropped = hotDropped() + unsettledDropped;
    if (records.length === 0 && extraDropped === 0) {
      return;
    }
    const heldBefore = ledgerHeld;
    const reason = unverified;
    let planned: SpoolMeta;
    try {
      planned = await backend.transact(sessionRef, (view) =>
        planAppend(view, sessionRef, records, { capacity, extraDropped, unverified: reason }),
      );
    } catch (error) {
      markUnverified(error instanceof SpoolError ? error.reason : 'write_failed');
      mode = 'memory';
      await persistUnverified();
      return;
    }

    // commit 뒤 되읽는다. 호출이 성공했다는 사실만으로 저장됐다고 보지 않는다.
    try {
      const readback = readMeta((await backend.list(sessionRef, 0)).meta, sessionRef);
      if (readback.next_id < planned.next_id || readback.dropped < planned.dropped) {
        throw new SpoolError('readback_mismatch');
      }
      meta = readback;
    } catch (error) {
      markUnverified(error instanceof SpoolError ? error.reason : 'read_failed');
      mode = 'memory';
      await persistUnverified();
      return;
    }

    hot.commit(records);
    foldedHotDropped = hot.snapshot().dropped;
    unsettledDropped = 0;
    let conflicted = false;
    try {
      conflicted = heldBefore && ledger.getItem(ownLedgerKey) === null;
    } catch {
      markUnverified('ledger_unavailable');
    }
    if (conflicted) {
      markUnverified('ledger_conflict');
      await persistUnverified();
    }
    writeLedger();
  }

  function enqueuePump(): Promise<void> {
    chain = chain.then(pump, pump);
    return chain;
  }

  function hotRecords(max: number): SpooledRecord[] {
    return hot.peek(max).map((record) => ({ id: hotIds.get(record) ?? 0, record }));
  }

  return {
    sessionRef,

    sink(record) {
      hotIds.set(record, nextHotId);
      nextHotId -= 1;
      hot.sink(record);
      writeLedger();
      if (!pumpScheduled) {
        pumpScheduled = true;
        schedule(() => {
          pumpScheduled = false;
          void enqueuePump();
        });
      }
    },

    ready() {
      return opened;
    },

    drain() {
      return enqueuePump();
    },

    async peek(max) {
      await enqueuePump();
      const out: SpooledRecord[] = [];
      if (backend !== undefined && max > 0) {
        try {
          const listed = await backend.list(sessionRef, max);
          meta = readMeta(listed.meta, sessionRef);
          for (const item of listed.records) {
            const value = item as Partial<SpooledRecord> | null;
            if (value === null || !isCount(value.id) || validateConfirmation(value.record).length > 0) {
              throw new SpoolError('corrupt');
            }
            out.push({ id: value.id, record: value.record as SaveConfirmation });
          }
        } catch (error) {
          markUnverified(error instanceof SpoolError ? error.reason : 'read_failed');
          out.length = 0;
        }
      }
      if (mode === 'memory') {
        out.push(...hotRecords(max - out.length));
      }
      return out.slice(0, max);
    },

    async pin(ids) {
      if (backend === undefined || mode === 'memory') {
        return;
      }
      const durable = ids.filter((id) => id > 0);
      try {
        meta = await backend.transact(sessionRef, (view) => planPin(view, sessionRef, durable));
      } catch (error) {
        markUnverified(error instanceof SpoolError ? error.reason : 'write_failed');
      }
    },

    async commit(ids) {
      const inHot = new Set(ids.filter((id) => id < 0));
      if (inHot.size > 0) {
        hot.commit(hot.peek(hot.size()).filter((record) => inHot.has(hotIds.get(record) ?? 0)));
        writeLedger();
      }
      const durable = ids.filter((id) => id > 0);
      if (backend === undefined || (durable.length === 0 && mode === 'memory')) {
        return;
      }
      try {
        meta = await backend.transact(sessionRef, (view) => planRemove(view, sessionRef, durable));
      } catch (error) {
        markUnverified(error instanceof SpoolError ? error.reason : 'write_failed');
      }
    },

    snapshot() {
      const dropped = (meta?.dropped ?? 0) + hotDropped() + unsettledDropped;
      const reason = unverified ?? meta?.reason ?? null;
      const state: SpoolState = reason !== null ? 'unverified' : dropped > 0 ? 'loss' : 'complete';
      return { pending: (meta?.count ?? 0) + hot.size(), dropped, state, reason, mode };
    },
  };
}

// ── 메모리 backend (검사용) ───────────────────────────────────────────────

export type TransactFault = 'throw' | 'silent';
export type ListFault = 'throw';

export interface MemorySpoolBackend extends SpoolBackend {
  readonly faults: { readonly transact: TransactFault[]; readonly list: ListFault[] };
  corruptMeta(sessionRef: string): void;
}

/**
 * IndexedDB와 같은 계약의 메모리 backend. 단위 검사가 여러 runtime의 공용 저장소로 쓴다.
 *
 * - `throw`: transaction이 abort된다(아무것도 쓰지 않음).
 * - `silent`: 성공처럼 끝나지만 쓰기가 사라진다(되읽기로만 알 수 있음).
 */
export function createMemorySpoolBackend(): MemorySpoolBackend {
  const sessions = new Map<string, { meta: unknown; records: Map<number, SaveConfirmation> }>();
  const faults = { transact: [] as TransactFault[], list: [] as ListFault[] };

  function table(sessionRef: string) {
    let found = sessions.get(sessionRef);
    if (found === undefined) {
      found = { meta: undefined, records: new Map() };
      sessions.set(sessionRef, found);
    }
    return found;
  }

  return {
    faults,
    corruptMeta(sessionRef) {
      table(sessionRef).meta = { schema: SPOOL_META_SCHEMA, broken: true };
    },
    async transact(sessionRef, plan) {
      const fault = faults.transact.shift();
      if (fault === 'throw') {
        throw new SpoolError('write_failed');
      }
      const current = table(sessionRef);
      const mutation = plan({ meta: current.meta, ids: [...current.records.keys()].sort((a, b) => a - b) });
      if (fault !== 'silent') {
        for (const id of mutation.remove) {
          current.records.delete(id);
        }
        for (const item of mutation.put) {
          current.records.set(item.id, item.record);
        }
        current.meta = mutation.meta;
      }
      return mutation.meta;
    },
    async list(sessionRef, limit) {
      if (faults.list.shift() === 'throw') {
        throw new SpoolError('read_failed');
      }
      const current = table(sessionRef);
      const records = [...current.records.entries()]
        .sort(([a], [b]) => a - b)
        .slice(0, limit)
        .map(([id, record]) => ({ id, record }));
      return { meta: current.meta, records };
    },
  };
}
